import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

import { clampPanelWidth, computeLayout } from './split-sizing-utils.js';
import { convertToPx, toCssLength, type PanelWidth } from './width-utils.js';
import { useResizeObserver } from '../../core/hooks/useResizeObserver.js';

// Read the effective flex space consumed by the SplitLayout resize handle.
// Under visual refresh, a CSS var is set to 0 on :root (see SplitLayout.css.ts).
// Reading from document.documentElement is synchronous and available before the
// handle element mounts, so the correct value is used from the very first render.
function getEffectiveHandleWidth(): number {
  if (typeof document === 'undefined') {
    return 8;
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--_dt-split-handle-ew')
    .trim();
  const value = parseFloat(raw);
  return Number.isNaN(value) ? 8 : value;
}

interface UseApplyControlledWidthOptions {
  controlledWidth: number | undefined;
  containerWidth: number;
  isDraggingRef: RefObject<boolean>;
  getClampedSplit: (split: number, container: number) => number;
  splitPxRef: RefObject<number>;
  hasBeenManuallyResizedRef: RefObject<boolean>;
  setSplitPx: Dispatch<SetStateAction<number>>;
}

function useApplyControlledWidth({
  controlledWidth,
  containerWidth,
  isDraggingRef,
  getClampedSplit,
  splitPxRef,
  hasBeenManuallyResizedRef,
  setSplitPx,
}: UseApplyControlledWidthOptions): void {
  const appliedRef = useRef<number | undefined>(undefined);
  if (
    controlledWidth !== undefined &&
    containerWidth > 0 &&
    !isDraggingRef.current &&
    controlledWidth !== appliedRef.current
  ) {
    appliedRef.current = controlledWidth;
    const clamped = getClampedSplit(controlledWidth, containerWidth);
    if (splitPxRef.current !== clamped) {
      splitPxRef.current = clamped;
      hasBeenManuallyResizedRef.current = true;
      setSplitPx(clamped);
    }
  }
}

interface UseSplitSizingOptions {
  panel: {
    min: PanelWidth;
    max: PanelWidth;
    default: PanelWidth;
    position: 'start' | 'end';
    resizable: boolean;
    onResize?: (width: number) => void;
    width?: number;
  };
  content: {
    min: PanelWidth;
  };
  refs: {
    root: RefObject<HTMLDivElement | null>;
    panel: RefObject<HTMLDivElement | null>;
    content: RefObject<HTMLDivElement | null>;
  };
}

interface SplitSizing {
  panel: {
    width: number;
    widthCss: string;
    minPx: number;
    maxPx: number;
  };
  content: {
    width: number;
    widthCss: string;
    minPx: number;
    maxPx: number;
  };
  handleWidth: number;
  dragActions: {
    onDragStart: () => void;
    onDrag: (mx: number) => void;
    onDragEnd: () => void;
  };
}

export function useSplitSizing(options: UseSplitSizingOptions): SplitSizing {
  const { panel, content, refs } = options;

  const [containerWidth, setContainerWidth] = useState(0);

  const [splitPx, setSplitPx] = useState<number>(() => {
    if (typeof panel.default === 'number') {
      return panel.default;
    }
    return -1;
  });

  const splitPxRef = useRef(splitPx);
  const isDraggingRef = useRef(false);
  const hasBeenManuallyResizedRef = useRef(false);
  const dragStartPx = useRef<number | null>(null);

  if (!isDraggingRef.current) {
    splitPxRef.current = splitPx;
  }

  const handleWidth = panel.resizable ? getEffectiveHandleWidth() : 0;

  const contentMinCss = toCssLength(content.min);

  const sizingConfig = useMemo(
    () => ({
      panelMin: panel.min,
      panelMax: panel.max,
      contentMin: content.min,
      handleWidth,
    }),
    [panel.min, panel.max, content.min, handleWidth],
  );

  const getClampedSplit = useCallback(
    (split: number, container: number) =>
      clampPanelWidth(split, container, sizingConfig),
    [sizingConfig],
  );

  useLayoutEffect(
    function initializeSplit() {
      if (containerWidth === 0) {
        return;
      }
      // For a numeric defaultWidth, initialize once and then let the user drive.
      if (typeof panel.default === 'number') {
        if (splitPx !== -1) {
          return;
        }
        setSplitPx(panel.default);
        return;
      }
      // For a percentage defaultWidth, re-derive from the container on every
      // container resize until the user has manually dragged. This ensures that
      // when the container itself is sized via %, the inner layout still applies
      // the percentage against the correct, fully-resolved container width.
      if (hasBeenManuallyResizedRef.current) {
        return;
      }
      setSplitPx(convertToPx(panel.default, containerWidth));
    },
    [containerWidth, panel.default, splitPx],
  );

  useApplyControlledWidth({
    controlledWidth: panel.width,
    containerWidth,
    isDraggingRef,
    getClampedSplit,
    splitPxRef,
    hasBeenManuallyResizedRef,
    setSplitPx,
  });

  useResizeObserver({
    // Unfortunately, the `useResizeObserver` hook doesn't support generic null types
    // so we have to cast here to avoid a type error.
    ref: refs.root as RefObject<Element>,
    onResize: (size) => {
      if (!size.width) {
        return;
      }
      setContainerWidth(size.width);
    },
  });

  const { position: panelPosition, onResize: onPanelResize } = panel;

  const dragActions = useMemo(
    () => ({
      onDragStart: () => {
        isDraggingRef.current = true;
        const containerW = refs.root.current?.offsetWidth ?? 0;
        const clamped = getClampedSplit(splitPxRef.current, containerW);
        splitPxRef.current = clamped;
        setSplitPx(clamped);
        dragStartPx.current = clamped;
      },
      onDrag: (mx: number) => {
        const containerW = refs.root.current?.offsetWidth ?? 0;
        if (dragStartPx.current === null || containerW === 0) {
          return;
        }

        const sign = panelPosition === 'start' ? 1 : -1;

        const next = dragStartPx.current + mx * sign;
        const clamped = getClampedSplit(next, containerW);
        splitPxRef.current = clamped;

        if (refs.panel.current) {
          refs.panel.current.style.width = `${clamped}px`;
        }
        if (refs.content.current) {
          refs.content.current.style.width = `max(${contentMinCss}, 100% - ${
            clamped + handleWidth
          }px)`;
        }
      },
      onDragEnd: () => {
        isDraggingRef.current = false;
        hasBeenManuallyResizedRef.current = true;
        dragStartPx.current = null;
        const rootWidth = refs.root.current?.offsetWidth ?? 0;
        const clamped = getClampedSplit(splitPxRef.current, rootWidth);
        splitPxRef.current = clamped;
        setSplitPx(clamped);
        onPanelResize?.(clamped);
      },
    }),
    [
      getClampedSplit,
      panelPosition,
      onPanelResize,
      handleWidth,
      contentMinCss,
      refs,
    ],
  );

  const layout = computeLayout(splitPx, containerWidth, sizingConfig);

  // computeLayout resolves percentage bounds against containerWidth; when it is
  // 0, those clamp to 0 and collapse the panel to minWidth.
  const isPreMeasure = containerWidth === 0 || splitPx === -1;
  const preWidthCss =
    panel.width !== undefined ? `${panel.width}px` : toCssLength(panel.default);
  const panelWidthCss = isPreMeasure ? preWidthCss : `${layout.panel.width}px`;
  const contentWidthCss = isPreMeasure
    ? `max(${toCssLength(
        content.min,
      )}, 100% - ${preWidthCss} - ${handleWidth}px)`
    : layout.content.widthCss;

  return {
    ...layout,
    panel: { ...layout.panel, widthCss: panelWidthCss },
    content: { ...layout.content, widthCss: contentWidthCss },
    handleWidth,
    dragActions,
  };
}
